import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SharedNav from '@/components/SharedNav';
import { API } from '@/App';
import { Leaf, Sprout, Microscope, GitBranch, Wheat, Waves, Flame, Layers, FolderUp, GitCompareArrows } from 'lucide-react';
import LeafCanopy from './phenoscan/LeafCanopy';
import Seed from './phenoscan/Seed';
import Disease from './phenoscan/Disease';
import Panicle from './phenoscan/Panicle';
import Root from './phenoscan/Root';
import NIR from './phenoscan/NIR';
import Heatmap from './phenoscan/Heatmap';
import Integrated from './phenoscan/Integrated';
import Batch from './phenoscan/Batch';
import Compare from './phenoscan/Compare';
import ComingSoonPane from './phenoscan/ComingSoonPane';

const SUB_TABS = [
  { key: 'leaf', label: 'Leaf & Canopy', icon: Leaf, active: true,
    blurb: 'Leaf area, shape, color (RGB/HSV/Lab), chlorophyll proxies, stress scoring, LAI.' },
  { key: 'seed', label: 'Seed & Fruit', icon: Sprout, active: true,
    blurb: 'Automatic seed counting (watershed), per-seed morphometrics, CV uniformity, defect detection.' },
  { key: 'disease', label: 'Disease & Lesion', icon: Microscope, active: true,
    blurb: 'Lesion area %, count, Horsfall-Barratt 0–11 scoring, chlorotic/necrotic classification.' },
  { key: 'root', label: 'Root Analysis', icon: GitBranch, active: true,
    blurb: 'Skeletonised root length, diameter, tip count, branching angles (RhizoVision algorithms).' },
  { key: 'panicle', label: 'Panicle / Spike', icon: Wheat, active: true,
    blurb: 'Panicle length/width, spikelet estimate, compactness index, branch count.' },
  { key: 'nir', label: 'NIR Prediction', icon: Waves, active: true,
    blurb: 'PLSR calibration models for moisture, oil, protein, starch, fiber, phenolics.' },
  { key: 'heatmap', label: 'Stress Heatmap', icon: Flame, active: true,
    blurb: 'Publication-ready overlays (moisture, chlorophyll loss, lesion intensity) on original images.' },
  { key: 'integrated', label: 'Integrated Analysis', icon: Layers, active: true,
    blurb: 'Hub: joins image traits + NIR + agro-morph + environment, then hands off to BKAgriD stats modules.' },
  { key: 'batch', label: 'Batch Upload', icon: FolderUp, active: true,
    blurb: 'Upload a folder of images, parse filenames for genotype/rep, run the chosen PhenoScan sub-tab on every image, export one trait CSV.' },
  { key: 'compare', label: 'Side-by-Side Compare', icon: GitCompareArrows, active: true,
    blurb: 'Compare two sample groups (drought vs control, diseased vs healthy, G1 vs G2) with auto-computed trait deltas, t-test / Mann-Whitney p-values, Cohen\'s d, and BH-FDR correction.' },
];

export default function PhenoScan() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const active = sp.get('tab') || 'leaf';
  const [flag, setFlag] = useState({ enabled: true });

  useEffect(() => {
    axios.get(`${API}/phenoscan/feature-flags`)
      .then(r => setFlag(r.data))
      .catch(() => setFlag({ enabled: true }));
  }, []);

  if (flag.enabled === false) {
    return (
      <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
        <SharedNav />
        <div className="max-w-[900px] mx-auto py-20 px-6 text-center">
          <h1 className="font-display text-3xl mb-4" style={{ color: '#1B4332' }}>PhenoScan is currently disabled</h1>
          <p className="text-[#4F4B44]">Contact your administrator. Set <code>ENABLE_PHENOSCAN=true</code> on the server.</p>
        </div>
      </div>
    );
  }

  const currentTab = SUB_TABS.find(t => t.key === active) || SUB_TABS[0];

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }} data-testid="phenoscan-page">
      <SharedNav />
      {/* Header */}
      <div className="max-w-[1320px] mx-auto px-6 pt-8 pb-4">
        <div className="flex items-center gap-3 mb-1" data-testid="phenoscan-header">
          <Leaf size={22} style={{ color: '#2D6A4F' }} strokeWidth={2.2} />
          <h1 className="font-display text-3xl" style={{ color: '#1B4332', letterSpacing: '-0.01em' }}>
            PhenoScan — Image &amp; Sensor Phenotyping
          </h1>
        </div>
        <p className="text-sm" style={{ color: '#4F4B44', maxWidth: 820 }}>
          Domain-specific plant phenotyping: leaves, seeds, roots, panicles, disease, NIR, heatmaps.
          Every run produces a calibrated trait matrix that hands off directly to BKAgriD statistics modules.
        </p>
      </div>

      {/* Sub-tab strip */}
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="flex gap-1 overflow-x-auto border-b" style={{ borderColor: '#E5E5E0' }} data-testid="phenoscan-subtabs">
          {SUB_TABS.map(t => {
            const Icon = t.icon;
            const isActive = t.key === active;
            return (
              <button
                key={t.key}
                onClick={() => setSp({ tab: t.key })}
                data-testid={`phenoscan-subtab-${t.key}`}
                className="flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap transition"
                style={{
                  color: isActive ? '#1B4332' : '#6F695F',
                  borderBottom: isActive ? '2px solid #2D6A4F' : '2px solid transparent',
                  fontWeight: isActive ? 600 : 500,
                  background: isActive ? '#F3F6F3' : 'transparent',
                }}
              >
                <Icon size={16} />
                {t.label}
                {!t.active && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ background: '#FEF3C7', color: '#92400E' }}>soon</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active pane */}
      <div className="max-w-[1320px] mx-auto px-6 py-8">
        {active === 'leaf' ? <LeafCanopy /> :
         active === 'seed' ? <Seed /> :
         active === 'disease' ? <Disease /> :
         active === 'panicle' ? <Panicle /> :
         active === 'root' ? <Root /> :
         active === 'nir' ? <NIR /> :
         active === 'heatmap' ? <Heatmap /> :
         active === 'integrated' ? <Integrated /> :
         active === 'batch' ? <Batch /> :
         active === 'compare' ? <Compare /> :
         <ComingSoonPane title={currentTab.label} blurb={currentTab.blurb} />}
      </div>
    </div>
  );
}
